// Ordenar la tabla de Pagos del Mes tocando el nombre de la columna.
//
// Lissette, 2026-09-01: "en modulo de pago tambien podria colocar para
// ordenarlo ascendente o descendente, menor a mayor?".
//
// Mismo gesto que en Tareas —un toque sube, otro baja, el tercero devuelve el
// orden de siempre— y con las mismas piezas: `ordenDeTabla.js`. Lo que cambia
// de una tabla a otra es de donde sale el valor de cada columna, y eso es lo
// unico que vive aqui.
//
// Modulo PLANO, fuera de PoliciesView.jsx, para que las pruebas puedan ordenar
// listas de verdad sin montar React (regla de oro 48).
import { compararValores, siguienteOrden, flechaDeOrden } from './ordenDeTabla.js';
import { FORMAS_DE_PAGO, METODOS_DE_PAGO } from './filtrosDePagosDelMes.js';

export { siguienteOrden, flechaDeOrden };

// Las columnas fijas, en el mismo orden en que se pintan. Las columnas propias
// de la agencia no estan aqui: su clave se arma con `claveDeColumnaPropia`.
export const COLUMNAS_DE_PAGOS = ['cliente', 'forma', 'metodo', 'nota', 'monto', 'estado'];

export const PREFIJO_COLUMNA_PROPIA = 'col:';
export const claveDeColumnaPropia = (id) => `${PREFIJO_COLUMNA_PROPIA}${id}`;

// Estado del pago, ordenado por lo que significa y no por alfabeto: primero lo
// que ya esta resuelto, al final lo que hay que perseguir. "Pago, Sin revisar,
// No pago" es una lista accionable; en alfabeto seria "No pago, Pago, Sin
// revisar", que no dice nada. Los estados propios de la agencia van detras,
// entre ellos por su nombre.
const RANGO_DE_ESTADO = { paid: 0, pending: 1, unpaid: 2 };

const texto = (v) => String(v ?? '').trim();

/**
 * El valor por el que ordena cada columna. Texto o numero, nunca null: la
 * cadena vacia es la marca de "sin dato" que manda la fila al final.
 *
 * `ctx` lo pone la vista, que es quien sabe resolver lo suyo:
 *   nombreDe(fila)        el cliente tal y como se pinta en la primera columna
 *   estadoDe(fila)        'paid' | 'unpaid' | 'pending' | id de estado propio
 *   etiquetaDeEstado(id)  como se lee un estado propio
 */
export function valorDeOrdenDePago(fila, key, ctx) {
  const { nombreDe, estadoDe, etiquetaDeEstado } = ctx || {};
  if (!fila || !key) return '';

  if (key.startsWith(PREFIJO_COLUMNA_PROPIA)) {
    const id = key.slice(PREFIJO_COLUMNA_PROPIA.length);
    return texto(fila.customData?.[id]).toLowerCase();
  }

  switch (key) {
    case 'cliente': return texto(nombreDe ? nombreDe(fila) : '').toLowerCase();
    // Forma y metodo por el orden del catalogo, que es el orden en que se leen
    // en el desplegable. Lo que no esta en el catalogo cuenta como sin definir:
    // la tabla tampoco le pinta chip, asi que para la agente esta vacio igual.
    case 'forma': {
      const i = FORMAS_DE_PAGO.indexOf(texto(fila.paymentType));
      return i < 0 ? '' : i;
    }
    case 'metodo': {
      const i = METODOS_DE_PAGO.indexOf(texto(fila.paymentMethod));
      return i < 0 ? '' : i;
    }
    // Cero notas es un dato, no un hueco: el 0 aqui SI ordena, y es justo lo
    // que se busca (que filas no tienen ninguna).
    case 'nota': {
      const n = Number(fila.payment?.notesCount);
      return Number.isFinite(n) ? n : 0;
    }
    // El monto es la MISMA prima mensual que pinta la columna. Sin monto no es
    // cero: es sin dato, y se va al final.
    case 'monto': {
      // Ojo con `Number(null)`: da 0, que es finito. Sin este corte una poliza
      // SIN monto se colaba la PRIMERA al ordenar de menor a mayor, como si
      // costara cero — justo al reves de lo prometido (lo vacio, al final).
      const bruto = fila.monthlyPremium;
      if (bruto === null || bruto === undefined || texto(bruto) === '') return '';
      const n = Number(bruto);
      return Number.isFinite(n) ? n : '';
    }
    case 'estado': {
      const id = texto(estadoDe ? estadoDe(fila) : '');
      if (!id) return '';
      const r = RANGO_DE_ESTADO[id];
      if (r !== undefined) return `${r}`;
      // Detras de los tres de fabrica, y entre ellos por su nombre.
      return `3${texto(etiquetaDeEstado ? etiquetaDeEstado(id) : id).toLowerCase()}`;
    }
    default: return '';
  }
}

export function compararPagosPorColumna(a, b, key, dir, ctx) {
  return compararValores(
    valorDeOrdenDePago(a, key, ctx),
    valorDeOrdenDePago(b, key, ctx),
    dir,
    !!(ctx && ctx.es),
  );
}

// El comparador completo. Sin columna elegida NO reordena nada: devuelve 0 y
// deja la lista como venia, que es el orden por defecto de esta tabla.
export function comparadorDePagos(sortBy, sortDir, ctx) {
  return (a, b) => (sortBy ? compararPagosPorColumna(a, b, sortBy, sortDir, ctx) : 0);
}
